import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, RefreshCw, Plus, Trash2, Play, AlertCircle, CheckCircle2 } from 'lucide-react';

const FREQ_LABELS = {
    '15m': 'Every 15 minutes',
    '30m': 'Every 30 minutes',
    '1h': 'Every hour',
    '6h': 'Every 6 hours',
    '24h': 'Once daily',
};

// Quick-add provider templates. Users edit URL & name after selecting.
const PROVIDER_PRESETS = [
    {
        id: 'reed',
        label: 'Reed',
        emoji: '🇬🇧',
        colour: 'bg-rose-50 text-rose-700 border-rose-200',
        country: 'UK',
        frequency: '1h',
        exampleUrl: 'https://www.reed.co.uk/jobs/software-developer-jobs-in-london',
        placeholderName: 'Reed – Software Developer London',
        hint: 'Reed job search listings — works well.',
    },
    {
        id: 'adzuna',
        label: 'Adzuna',
        emoji: '🇬🇧',
        colour: 'bg-sky-50 text-sky-700 border-sky-200',
        country: 'UK',
        frequency: '1h',
        exampleUrl: 'https://www.adzuna.co.uk/search?q=software+engineer&loc=London',
        placeholderName: 'Adzuna – Engineer London',
        hint: 'May be rate-limited on some queries.',
    },
    {
        id: 'indeed',
        label: 'Indeed UK',
        emoji: '🇬🇧',
        colour: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        country: 'UK',
        frequency: '6h',
        exampleUrl: 'https://uk.indeed.com/jobs?q=software+engineer&l=London',
        placeholderName: 'Indeed – Engineer London',
        hint: 'Indeed has strong bot-protection; may return 0 results.',
    },
    {
        id: 'linkedin',
        label: 'LinkedIn',
        emoji: '🌐',
        colour: 'bg-blue-50 text-blue-700 border-blue-200',
        country: 'UK',
        frequency: '6h',
        exampleUrl: 'https://www.linkedin.com/jobs/search/?keywords=software%20engineer&location=London',
        placeholderName: 'LinkedIn – Engineer London',
        hint: 'Public guest feed — up to 100 jobs per sync, no browser needed.',
    },
    {
        id: 'nhs',
        label: 'NHS Jobs',
        emoji: '🏥',
        colour: 'bg-teal-50 text-teal-700 border-teal-200',
        country: 'UK',
        frequency: '6h',
        exampleUrl: 'https://www.jobs.nhs.uk/candidate/search/results?keyword=nurse&location=London',
        placeholderName: 'NHS – Nurse London',
        hint: 'Official NHS feed — up to 5 pages per sync.',
    },
    {
        id: 'jobs-ac-uk',
        label: 'jobs.ac.uk',
        emoji: '🎓',
        colour: 'bg-amber-50 text-amber-700 border-amber-200',
        country: 'UK',
        frequency: '6h',
        exampleUrl: 'https://www.jobs.ac.uk/search/?keywords=lecturer&location=London',
        placeholderName: 'jobs.ac.uk – Lecturer London',
        hint: 'Universities & research roles — works well.',
    },
    {
        id: 'civil-service',
        label: 'Civil Service',
        emoji: '🇬🇧',
        colour: 'bg-violet-50 text-violet-700 border-violet-200',
        country: 'UK',
        frequency: '24h',
        exampleUrl: 'https://uk.linkedin.com/jobs/search?keywords=HMRC&location=United%20Kingdom&employer=HM%20Revenue',
        placeholderName: 'Civil Service – HMRC',
        hint: 'Civil Service jobs via LinkedIn. Use employer= to keep only that department.',
    },
    {
        id: 'gov-uk',
        label: 'Gov.uk / Council',
        emoji: '🇬🇧',
        colour: 'bg-slate-50 text-slate-700 border-slate-200',
        country: 'UK',
        frequency: '24h',
        exampleUrl: 'https://www.jgp.co.uk/jobs',
        placeholderName: 'Gov / Council jobs',
        hint: 'Councils and gov.uk-hosted job boards.',
    },
    {
        id: 'generic',
        label: 'Any agency site',
        emoji: '🔗',
        colour: 'bg-neutral-50 text-neutral-700 border-neutral-200',
        country: 'UK',
        frequency: '6h',
        exampleUrl: 'https://example-agency.com/careers',
        placeholderName: 'Agency careers page',
        hint: 'Uses a generic parser. Best-effort scraping.',
    },
];

const STATUS_ICONS = {
    success: <span className="inline-flex items-center gap-1 text-emerald-600"><span className="text-lg leading-none">🟢</span> Working</span>,
    warning: <span className="inline-flex items-center gap-1 text-amber-600"><span className="text-lg leading-none">🟡</span> Warning</span>,
    error: <span className="inline-flex items-center gap-1 text-rose-600"><span className="text-lg leading-none">🔴</span> Error</span>,
    pending: <span className="inline-flex items-center gap-1 text-neutral-500"><span className="text-lg leading-none">⚪</span> Pending</span>,
};

const backend = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
    };
}

export const JobSourcesPanel = () => {
    const [sources, setSources] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncingIds, setSyncingIds] = useState({});
    const [showAdd, setShowAdd] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState('reed');
    const [form, setForm] = useState({
        name: '',
        url: '',
        country: 'UK',
        frequency: '1h',
        agency_name: '',
    });

    const applyPreset = (preset) => {
        setSelectedPreset(preset.id);
        setForm((prev) => ({
            ...prev,
            country: preset.country,
            frequency: preset.frequency,
            // Only fill URL/name if the user hasn't started typing
            url: (!prev.url || PROVIDER_PRESETS.some(p => p.exampleUrl === prev.url)) ? preset.exampleUrl : prev.url,
            name: (!prev.name || PROVIDER_PRESETS.some(p => p.placeholderName === prev.name)) ? preset.placeholderName : prev.name,
        }));
    };

    const openAddDialog = () => {
        setSelectedPreset('reed');
        setForm({
            name: PROVIDER_PRESETS[0].placeholderName,
            url: PROVIDER_PRESETS[0].exampleUrl,
            country: PROVIDER_PRESETS[0].country,
            frequency: PROVIDER_PRESETS[0].frequency,
            agency_name: '',
        });
        setShowAdd(true);
    };

    const currentPreset = PROVIDER_PRESETS.find(p => p.id === selectedPreset) || PROVIDER_PRESETS[0];

    const load = async () => {
        try {
            const [srcRes, dashRes] = await Promise.all([
                fetch(`${backend}/api/admin/job-sources`, { headers: authHeaders() }),
                fetch(`${backend}/api/admin/sync-dashboard`, { headers: authHeaders() }),
            ]);
            const srcJson = await srcRes.json();
            const dashJson = await dashRes.json();
            setSources(srcJson.sources || []);
            setDashboard(dashJson);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load auto-sync data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // Refresh every 30 s so the dashboard reflects background scheduler activity
        const iv = setInterval(load, 30000);
        return () => clearInterval(iv);
    }, []);

    const handleCreate = async () => {
        if (!form.name.trim() || !form.url.trim()) {
            toast.error('Name and URL are required');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${backend}/api/admin/job-sources`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify(form),
            });
            const j = await res.json();
            if (res.ok) {
                toast.success('Job source added');
                setShowAdd(false);
                setSelectedPreset('reed');
                setForm({ name: '', url: '', country: 'UK', frequency: '1h', agency_name: '' });
                load();
            } else {
                toast.error(j.detail || 'Failed to create source');
            }
        } catch (e) {
            toast.error('Network error');
        } finally {
            setSaving(false);
        }
    };

    const handleSyncNow = async (id) => {
        setSyncingIds((prev) => ({ ...prev, [id]: true }));
        try {
            const res = await fetch(`${backend}/api/admin/job-sources/${id}/sync-now`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const j = await res.json();
            if (res.ok) {
                const s = j.stats || {};
                toast.success(`Sync complete: +${s.added || 0} new, ${s.updated || 0} updated, ${s.closed || 0} closed, ${s.duplicates || 0} duplicates`);
                load();
            } else {
                toast.error(j.detail || 'Sync failed');
            }
        } catch (e) {
            toast.error('Sync failed');
        } finally {
            setSyncingIds((prev) => ({ ...prev, [id]: false }));
        }
    };

    const handleToggleActive = async (source) => {
        try {
            const res = await fetch(`${backend}/api/admin/job-sources/${source.source_id}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ is_active: !source.is_active }),
            });
            if (res.ok) {
                toast.success(source.is_active ? 'Source paused' : 'Source resumed');
                load();
            }
        } catch (e) {
            toast.error('Failed to update source');
        }
    };

    const handleUpdateFrequency = async (source, freq) => {
        try {
            const res = await fetch(`${backend}/api/admin/job-sources/${source.source_id}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ frequency: freq }),
            });
            if (res.ok) {
                toast.success('Frequency updated');
                load();
            }
        } catch (e) {
            toast.error('Failed to update');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this job source? Jobs already imported will remain.')) return;
        try {
            const res = await fetch(`${backend}/api/admin/job-sources/${id}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (res.ok) {
                toast.success('Source deleted');
                load();
            }
        } catch (e) {
            toast.error('Failed to delete');
        }
    };

    const fmtDate = (iso) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
        } catch { return iso; }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const t = dashboard?.totals_today || {};
    const sc = dashboard?.status_counts || {};

    return (
        <div className="space-y-6">
            {/* Dashboard summary */}
            <Card data-testid="auto-sync-dashboard">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-primary-600" /> Auto-Sync Dashboard
                    </CardTitle>
                    <CardDescription>
                        Jobs are synced automatically in the background. No manual updates needed.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        <StatBox label="Sources" value={dashboard?.total_sources || 0} />
                        <StatBox label="Added today" value={t.added || 0} tone="emerald" />
                        <StatBox label="Updated today" value={t.updated || 0} tone="sky" />
                        <StatBox label="Closed today" value={t.closed || 0} tone="rose" />
                        <StatBox label="Duplicates today" value={t.duplicates || 0} tone="amber" />
                        <StatBox label="Feed errors" value={sc.error || 0} tone={sc.error ? 'rose' : 'neutral'} />
                    </div>

                    <div className="mt-4 flex flex-wrap gap-4 text-sm text-neutral-600" data-testid="sync-times">
                        <span>Last successful sync: <strong>{fmtDate(dashboard?.last_success_at)}</strong></span>
                        <span>Next scheduled sync: <strong>{fmtDate(dashboard?.next_sync_at)}</strong></span>
                    </div>
                </CardContent>
            </Card>

            {/* Sources table */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle>Job Sources ({sources.length})</CardTitle>
                        <CardDescription>Each source is scraped on its schedule and imported automatically.</CardDescription>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={load} data-testid="refresh-sources-btn">
                            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                        </Button>
                        <Button onClick={openAddDialog} className="bg-primary-600 hover:bg-primary-700" data-testid="add-source-btn">
                            <Plus className="w-4 h-4 mr-1" /> Add Source
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Supported providers hint */}
                    <div className="mb-4 p-3 bg-primary-50/50 border border-primary-100 rounded-lg">
                        <div className="text-xs font-medium text-primary-800 mb-2">✨ Supported providers — click "Add Source" to pick one:</div>
                        <div className="flex flex-wrap gap-1.5">
                            {PROVIDER_PRESETS.map((p) => (
                                <span key={p.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${p.colour}`}>
                                    <span>{p.emoji}</span> {p.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {sources.length === 0 ? (
                        <div className="text-center py-8 text-neutral-500" data-testid="no-sources-msg">
                            No job sources yet. Add one to start automatic synchronisation.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>URL</TableHead>
                                    <TableHead>Country</TableHead>
                                    <TableHead>Frequency</TableHead>
                                    <TableHead>Last sync</TableHead>
                                    <TableHead>Next sync</TableHead>
                                    <TableHead>Today</TableHead>
                                    <TableHead>Active</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sources.map((s) => {
                                    const st = s.stats_today || {};
                                    const isSyncing = !!syncingIds[s.source_id];
                                    return (
                                        <TableRow key={s.source_id} data-testid={`source-row-${s.source_id}`}>
                                            <TableCell>{STATUS_ICONS[s.last_status] || STATUS_ICONS.pending}</TableCell>
                                            <TableCell className="font-medium max-w-[220px] truncate" title={s.name}>{s.name}</TableCell>
                                            <TableCell className="max-w-[260px] truncate" title={s.url}>
                                                <a href={s.url} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline text-xs">{s.url}</a>
                                            </TableCell>
                                            <TableCell>{s.country === 'Canada' ? '🇨🇦 CA' : '🇬🇧 UK'}</TableCell>
                                            <TableCell>
                                                <Select value={s.frequency} onValueChange={(v) => handleUpdateFrequency(s, v)}>
                                                    <SelectTrigger className="w-[150px] h-8 text-xs" data-testid={`freq-select-${s.source_id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {Object.entries(FREQ_LABELS).map(([k, v]) => (
                                                            <SelectItem key={k} value={k}>{v}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(s.last_sync_at)}</TableCell>
                                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(s.next_sync_at)}</TableCell>
                                            <TableCell className="text-xs">
                                                <div className="flex gap-1 flex-wrap">
                                                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">+{st.added || 0}</Badge>
                                                    <Badge variant="secondary" className="bg-sky-50 text-sky-700">~{st.updated || 0}</Badge>
                                                    <Badge variant="secondary" className="bg-rose-50 text-rose-700">−{st.closed || 0}</Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="sm"
                                                    variant={s.is_active ? 'default' : 'outline'}
                                                    onClick={() => handleToggleActive(s)}
                                                    className={s.is_active ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                                                    data-testid={`toggle-active-${s.source_id}`}
                                                >
                                                    {s.is_active ? 'On' : 'Off'}
                                                </Button>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleSyncNow(s.source_id)}
                                                        disabled={isSyncing}
                                                        title="Sync now"
                                                        data-testid={`sync-now-${s.source_id}`}
                                                    >
                                                        {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleDelete(s.source_id)}
                                                        className="text-rose-600 hover:bg-rose-50"
                                                        title="Delete source"
                                                        data-testid={`delete-source-${s.source_id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}

                    {/* Show recent error rows for quick visibility */}
                    {sources.some((s) => s.last_status === 'error') && (
                        <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-lg" data-testid="feed-errors-banner">
                            <div className="flex items-center gap-2 font-medium text-rose-700 mb-2">
                                <AlertCircle className="w-4 h-4" /> Feed errors need your attention
                            </div>
                            <ul className="text-sm text-rose-700 list-disc list-inside space-y-1">
                                {sources.filter((s) => s.last_status === 'error').map((s) => (
                                    <li key={s.source_id}>
                                        <strong>{s.name}</strong> — {s.last_error?.slice(0, 200) || 'Unknown error'} (fails: {s.consecutive_failures})
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Add source dialog */}
            <Dialog open={showAdd} onOpenChange={setShowAdd}>
                <DialogContent className="max-w-2xl" data-testid="add-source-dialog">
                    <DialogHeader>
                        <DialogTitle>Add Job Source</DialogTitle>
                        <DialogDescription>
                            Pick a provider below to pre-fill the form, then tweak the URL to your specific search.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Provider preset picker */}
                    <div className="py-2">
                        <label className="text-xs uppercase tracking-wide text-neutral-500 font-medium">Provider</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {PROVIDER_PRESETS.map((p) => {
                                const active = selectedPreset === p.id;
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => applyPreset(p)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition ${active
                                            ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                            : `${p.colour} hover:brightness-95`
                                        }`}
                                        data-testid={`preset-${p.id}`}
                                    >
                                        <span>{p.emoji}</span> {p.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-xs text-neutral-500 mt-2">{currentPreset.hint}</p>
                    </div>

                    <div className="space-y-3 py-2 border-t border-neutral-100">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Source name *</label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder={currentPreset.placeholderName}
                                data-testid="source-name-input"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">URL *</label>
                            <Input
                                value={form.url}
                                onChange={(e) => setForm({ ...form, url: e.target.value })}
                                placeholder={currentPreset.exampleUrl}
                                data-testid="source-url-input"
                            />
                            <p className="text-xs text-neutral-500 mt-1">
                                Tip: change search keywords / location in the URL to control what gets imported.
                            </p>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Agency label (optional)</label>
                            <Input
                                value={form.agency_name}
                                onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                                placeholder="Agency to attribute jobs to (defaults to scraped company)"
                                data-testid="source-agency-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Country</label>
                                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                                    <SelectTrigger data-testid="source-country-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                                        <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Frequency</label>
                                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                                    <SelectTrigger data-testid="source-frequency-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(FREQ_LABELS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAdd(false)} disabled={saving}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={saving} className="bg-primary-600 hover:bg-primary-700" data-testid="save-source-btn">
                            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving</> : 'Add Source'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const StatBox = ({ label, value, tone = 'neutral' }) => {
    const tones = {
        neutral: 'bg-neutral-50 text-neutral-700',
        emerald: 'bg-emerald-50 text-emerald-700',
        sky: 'bg-sky-50 text-sky-700',
        rose: 'bg-rose-50 text-rose-700',
        amber: 'bg-amber-50 text-amber-700',
    };
    return (
        <div className={`rounded-lg p-3 ${tones[tone] || tones.neutral}`}>
            <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
            <div className="text-2xl font-bold">{value}</div>
        </div>
    );
};

export default JobSourcesPanel;
